<template>
  <div>
    <DataTableWrapper
      ref="tableWrapper"
      :initialSortBy="[{ field: 'createdAt', dir: 'desc' }]"
      :data-source="{
        type: 'AxisGQL',
        query: require('@/services/gql/types/OrderBooking/queries/orderBookingGrid.gql')
      }"
      :filters="{
        status: {
          control: 'select',
          options: [...$options.status],
          dataType: String
        },
        createdAt: { control: 'date-range' }
      }"
      :headers="$options.headersConfig"
      @query-state-updated="getStateQuery"
      :stored-headers="getCurrentViewCols"
      :views-list="getViewList"
      :active-view-key="getActiveViewKey"
      :views-loading="$async.updateMe.$pending"
      :force-where="gridForceWhere"
      @view-changed="updateStoredView"
      @active-view-changed="updateStoredActiveView"
      @remove-view="removeStoredView"
      @data-loaded="onTableDataChange"
    >
      <template v-slot="{ tableProps, tableListeners, filtersProps, filtersListeners }">
        <v-data-table
          fixed-header
          height="80vh"
          :items-per-page="$options.perPage.itemsPerPage"
          :footer-props="{ itemsPerPageOptions: $options.perPage.itemsPerPageOptions }"
          v-bind="tableProps"
          v-on="tableListeners"
          v-model="mixSelectedItems"
          @update:sort-by="updateSortBy"
          @update:sort-desc="sortDesc = $event"
        >
          <template v-for="(h, i) in $options.headersConfig" v-slot:[`header.${h.value}`]>
            <v-tooltip open-delay="350" :key="i" bottom>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ h.text }}</span>
              </template>
              <span>{{ h.description }}</span>
            </v-tooltip>
          </template>
          <template v-slot:[`body.prepend`]="{}">
            <DataTableFilters
              is-grid-selectable
              v-bind="filtersProps"
              v-on="filtersListeners"
            />
          </template>
          <template
            v-for="(h, i) in $options.headersConfig"
            v-slot:[`item.${h.value}`]="{ item }"
          >
            <div :key="i">
              <div v-if="h.type === 'number' && item[h.value] !== null">
                {{ formatAmount(item[h.value]) }}
              </div>
              <div v-else-if="h.type === 'date'">
                <DateTimeCell :value="item[h.value]" />
              </div>
              <div v-else-if="h.type === 'boolean'">
                <v-icon
                  v-text="'mdi-checkbox-marked-circle'"
                  :color="item[h.value] ? 'success' : 'grey'"
                />
              </div>
              <div v-else-if="h.value === 'guests'">
                <router-link
                  v-if="item.channel === 'WEBPORTAL' && item[h.value][0]?.id"
                  title="Редактировать"
                  :to="{
                    name: 'page-account',
                    params: { id: item[h.value][0].id }
                  }"
                >{{ item[h.value][0].firstName || item[h.value][0].email || convertToPhone(item[h.value][0].phone) }}
                  {{ item[h.value][0].lastName }}</router-link>
                <div v-if="item.channel === 'MANUAL' && item.note?.length > 0">
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on">
                        <v-icon color="grey lighten-1">
                          mdi-comment-processing-outline
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>{{ item.note }}</span>
                  </v-tooltip>
                </div>
              </div>
              <div v-else-if="h.value === 'roomType'">
                <router-link
                  title="Редактировать"
                  :to="{
                    name: 'page-hotel',
                    params: { id: item[h.value].hotel.id }
                  }"
                >{{ item[h.value].hotel.title }}</router-link>
              </div>
              <div v-else-if="h.value === 'room'">
                {{ item.roomType.title }}
                <template v-if="item[h.value]?.floor && item[h.value]?.number">
                  (Этаж {{ item[h.value].floor }}, номер {{ item[h.value].number }})
                </template>
              </div>
              <div v-else-if="h.value === 'price'">
                <p v-if="item[h.value] > 0">
                  {{ item.price.toPrice('₽') }}
                </p>
              </div>
              <div v-else-if="h.value === 'status'">
                {{ $options.status.find(status => status.value === item.status).text }}
              </div>
              <div
                v-else-if="h.type === 'actions'"
                :style="{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end'
                }"
              >
                <v-btn
                  icon
                  :disabled="item.status === 'PAID'"
                  @click="deleteOrder(item.id)"
                >
                  <v-icon color="red">mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
              <div v-else>
                {{ get(item, h.value) }}
              </div>
            </div>
          </template>
        </v-data-table>
      </template>
    </DataTableWrapper>
    <ConfirmationDialog ref="confirmOrderBookingRemoving" confirmText="Удалить" />
  </div>
</template>

<script>
import { formatAmount, get } from '@/utils'
import updateMeMutation from '@/services/gql/types/Me/mutations/updateMe.gql'
import deleteReservationMutation from '@/services/gql/types/OrderBooking/mutations/deleteOrder.gql'
import perPage from '@/configs/v-data-table.js'
import headersConfig from './headersConfig.js'
import DataTableWrapper from '@/components/data-table-wrapper'
import DataTableFilters from '@/components/data-table-filters'
import DateTimeCell from '@/components/date-time-cell'
import ConfirmationDialog from '@/components/confirmation-dialog'
import status from '@/configs/booking-reservation-statuses.js'
import { paramsToListQueryInput } from '@/components/data-table-wrapper/data-sources/axis-gql/utils'
import { gridDependentMixin } from '@/pages/grid-dependent.mix'
import { convertToPhone } from '@/configs/numbers'
import '@/configs/toPrice'

export default {
  name: 'GridOrdersBooking',
  gridType: 'ordersBooking',
  perPage,
  status,
  headersConfig,
  mixins: [gridDependentMixin],
  components: {
    DataTableWrapper,
    DataTableFilters,
    DateTimeCell,
    ConfirmationDialog
  },
  computed: {
    gridForceWhere () {
      const id = Number(this.$route.query?.id)
      if (id) {
        return { room: { roomType: { hotel: { id } } } }
      }
      return {}
    }
  },
  data () {
    return {
      queryState: null,
      sortDesc: false,
      initialSortBy: []
    }
  },
  methods: {
    get,
    convertToPhone,
    async deleteOrder (id) {
      try {
        await this.$refs.confirmOrderBookingRemoving.open()
        await this.$async.deleteReservation.$perform({ id })
        this.$successMsg('Резервация удалена')
        this.refetch()
      } catch (e) {
        if (e.cancel) return
        this.$handleApiError(e, `Ошибка при удалении резервации с ID ${id}`)
      }
    },
    refetch () {
      this.$refs.tableWrapper.refresh()
    },
    formatAmount,
    getStateQuery (state) {
      this.queryState = paramsToListQueryInput(state)
    },
    updateSortBy ([field]) {
      this.$set(this.initialSortBy, 0, {
        field,
        dir: this.sortDesc[0] ? 'desc' : 'asc'
      })
      this.refetch()
    }
  },
  async: {
    deleteReservation (variables) {
      return this.$apollo.mutate({
        mutation: deleteReservationMutation,
        variables
      })
    },
    updateMe (variables) {
      return this.$apollo.mutate({
        mutation: updateMeMutation,
        variables
      })
    }
  }
}
</script>
